import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import SiteNav, { BookingButton, WriteMeLink } from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import { pageHead, SITE_URL } from "@/lib/seo";

const title = "Services de Fractional CMO : stratégie, CRM et IA | CMO Partner";
const description = "Growth Architecture, CRM & Lifecycle, IA & Agentic Marketing : trois missions de direction marketing externalisée pour PME et ETI, en format léger, standard ou intensif.";

const services = [
  {
    name: "Growth Architecture",
    path: "/services/growth-architecture" as const,
    description: "Relier vos contenus, votre audience et votre base clients à un pipeline mesurable. Structurer la conversion, le scoring et le pilotage de la croissance.",
  },
  {
    name: "CRM & Lifecycle Marketing",
    path: "/services/crm-lifecycle" as const,
    description: "Déployer ou refondre votre CRM et aligner marketing et ventes. Activer vos contacts avec des séquences, des automatisations et un suivi partagé.",
  },
  {
    name: "IA & Agentic Marketing",
    path: "/services/ai-marketing" as const,
    description: "Identifier les tâches répétitives et construire des agents IA utiles. Intégrer l'automatisation à vos outils et processus marketing existants.",
  },
];

const contexts = [
  { name: "Fractional CMO", path: "/fractional-cmo" as const },
  { name: "Directeur marketing externalisé", path: "/directeur-marketing-externalise" as const },
  { name: "Fractional CMO B2B SaaS", path: "/fractional-cmo-b2b-saas" as const },
  { name: "Fractional CMO B2C & E-commerce", path: "/fractional-cmo-b2c-ecommerce" as const },
  { name: "Fractional CMO à Bordeaux", path: "/fractional-cmo-bordeaux" as const },
];

export const Route = createFileRoute("/services/")({
  head: () => {
    const base = pageHead({ path: "/services", title, description, enPath: null });
    return ({
    ...base,
    meta: [
      ...base.meta,
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: services.map((service, index) => ({
            "@type": "ListItem", position: index + 1,
            item: { "@type": "Service", name: service.name, url: `${SITE_URL}${service.path}` },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/services` },
          ],
        }),
      },
    ],
  });
  },
  component: ServicesHub,
});

function ServicesHub() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteNav />
      <main>
        <section className="border-b border-border px-5 pb-16 pt-20 md:pb-20 md:pt-28">
          <div className="mx-auto max-w-4xl">
            <nav className="mb-8 text-sm text-muted-foreground" aria-label="Fil d'Ariane">
              <Link to="/" className="transition-colors hover:text-primary">Accueil</Link>
              <span className="px-2">/</span>
              <span className="text-foreground">Services</span>
            </nav>
            <h1 className="text-4xl font-bold leading-[1.1] md:text-6xl">
              Services de Fractional CMO : stratégie, CRM et IA<span className="text-primary">.</span>
            </h1>
            <p className="mt-8 text-lg leading-relaxed text-muted-foreground md:text-xl">
              Trois missions, un même objectif : transformer vos actifs marketing en croissance mesurable. Chacune se mène en format léger (2 à 4 jours par mois), standard (4 à 6 jours) ou intensif (8 à 12 jours), selon vos enjeux du moment.
            </p>
          </div>
        </section>

        <section className="px-5 py-20 md:py-24" aria-label="Les missions">
          <div className="mx-auto grid max-w-5xl gap-5 md:grid-cols-3">
            {services.map((service, index) => (
              <Link key={service.path} to={service.path} className="flex flex-col rounded-lg border border-border p-7 transition-colors hover:border-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
                <span className="text-sm font-semibold text-primary">0{index + 1}</span>
                <h2 className="mt-5 text-2xl font-bold leading-tight">{service.name}<span className="text-primary">.</span></h2>
                <p className="mt-5 flex-1 leading-relaxed text-muted-foreground">{service.description}</p>
                <span className="mt-8 inline-flex items-center gap-2 font-semibold text-primary">Découvrir la mission <ArrowRight className="h-4 w-4" aria-hidden="true" /></span>
              </Link>
            ))}
          </div>
        </section>

        <section className="border-t border-border bg-secondary px-5 py-20">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold md:text-4xl">Selon votre contexte<span className="text-primary">.</span></h2>
            <div className="mt-8 grid gap-x-10 md:grid-cols-2">
              {contexts.map((context) => (
                <Link key={context.path} to={context.path} className="flex items-center justify-between gap-4 border-t border-border py-5 font-semibold transition-colors hover:text-primary">
                  {context.name}<ArrowRight className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section id="booking" className="scroll-mt-16 bg-foreground px-5 py-24 text-center text-background md:py-28">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold md:text-5xl">Parlons de votre situation<span className="text-primary">.</span></h2>
            <p className="mt-6 text-lg text-background/60">30 minutes pour identifier vos leviers prioritaires.</p>
            <div className="mt-10"><BookingButton label="Réserver un appel" location="services_hub_final" large /><WriteMeLink /></div>
            <p className="mt-6 text-sm text-background/50">Basée à Bordeaux · Disponible partout en France</p>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}